import type { Mutation } from '@tanstack/react-query';

import { FormattedMessage } from 'react-intl';
import { enqueueSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';

import BaseApiResponseError from '../api/http/BaseApiResponseError';
import { isQueryErrorToastSuppressed } from './authErrorToast';
import { SessionMovedError } from '../api/sessionCookie';
import { isOwnedByChatRecovery } from '../api/chatRecoveryOwnership';
import messages from '../pages/messages';

/**
 * What happens when a mutation or a query fails and NOBODY handled it.
 *
 * Two families of failure reach React Query's caches, and only one of them had
 * an owner:
 *
 *   * an API REFUSAL (`BaseApiResponseError` and its subclasses) is an answer
 *     the backend meant to give, and `AuthProvider`'s two cache subscriptions
 *     already speak for it — they translate the server's own sentence, drop the
 *     ones the user must not be told about (`shouldToastApiError`) and end the
 *     session on the terminal codes. Nothing here may touch that family: a
 *     second toast would double every 403, and a Sentry event per refusal would
 *     bury real faults under 404s;
 *   * everything ELSE — a `TypeError` in a mapper, a Zod parse that did not
 *     survive a backend field rename, an upload that blew a size limit, a
 *     rejected `FileReader` — had no owner at all. Both caches were built with
 *     an empty config and 126 of the app's 173 mutations define no `onError`,
 *     so those failures produced nothing: no toast, no event, no console line.
 *     The user pressed a button and the app simply did not move.
 *
 * This module owns that second family, and only it.
 */

/**
 * What a failed mutation or query is allowed to say nothing about.
 *
 *   * an API refusal is an answer the backend meant to give, and `AuthProvider`
 *     already speaks for it;
 *   * `SessionMovedError` is thrown on every sign-out and on every tenant
 *     switch that lands while a renewal is in flight — its own doc calls it
 *     "the one rejection that is not news". Reporting it would put a Sentry
 *     event and a red box on the way OUT of the app.
 */
function isExpectedFailure(error: unknown) {
  return (
    error instanceof BaseApiResponseError || error instanceof SessionMovedError
  );
}

/**
 * One key for the whole family, so notistack's `preventDuplicate` can collapse
 * a burst (a retried save, a list where every row's mutation fails the same
 * way) into the one box that says the same thing. A React element as `message`
 * is a fresh object each time and would defeat the message-based comparison.
 */
export const GLOBAL_ERROR_SNACKBAR_KEY = 'global-error';

/**
 * `meta.silent` is the opt-out this layer introduces; `meta.suppressErrorToast`
 * is the one the app already had for the same intent ("this mutation draws its
 * own error UI", boolean or a predicate over the error and the variables).
 * Both are honoured, because opting out of one global toast must not silently
 * opt you into another.
 */
export function isGlobalErrorToastSuppressed(
  meta: Record<string, unknown> | undefined,
  error: unknown,
  variables: unknown,
) {
  if (meta?.silent === true) return true;

  const suppressErrorToast = meta?.suppressErrorToast;
  if (suppressErrorToast === true) return true;
  if (typeof suppressErrorToast === 'function') {
    return Boolean(
      (suppressErrorToast as (error: unknown, variables: unknown) => unknown)(
        error,
        variables,
      ),
    );
  }
  return false;
}

export function handleGlobalMutationError(
  error: unknown,
  variables: unknown,
  _context: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>,
) {
  // Not ours — see `isExpectedFailure`.
  if (isExpectedFailure(error)) return;

  // The report is unconditional: a code fault is worth an event whether or not
  // the mutation chose to draw its own message about it. The one exception is
  // a chat failure the Twilio lifecycle is already recovering from — a symptom
  // of an outage it reports itself — which still gets the toast below.
  if (!isOwnedByChatRecovery(error)) Sentry.captureException(error);

  // A FALLBACK, not a second voice. A mutation with its own `onError` has
  // already said something specific about this failure — the generic sentence
  // on top of it would be two red boxes for one broken button.
  if (typeof mutation.options.onError === 'function') return;
  if (isGlobalErrorToastSuppressed(mutation.options.meta, error, variables)) {
    return;
  }

  enqueueSnackbar({
    key: GLOBAL_ERROR_SNACKBAR_KEY,
    // A node, not a string: this runs outside React (the QueryClient is built
    // at module scope), and notistack renders the message INSIDE the provider
    // tree, where the app's `IntlProvider` is.
    message: <FormattedMessage {...messages.routeErrorTitle} />,
    variant: 'error',
  });
}

export function handleGlobalQueryError(
  error: unknown,
  query?: { meta?: Record<string, unknown>; state: { error: unknown } },
) {
  if (isExpectedFailure(error)) return;
  // A polled read that opted out of repeats (`meta.suppressErrorToast`): the
  // same fault every 30 s is one event, not one per poll.
  if (query && isQueryErrorToastSuppressed(query, 'error')) return;

  // Report only. A query is a read the user did not ask for by hand, and the
  // page that wanted the data renders its own empty or error state — a toast
  // per failed background read is the burst `shouldToastApiError` was written
  // to stop.
  if (isOwnedByChatRecovery(error)) return;
  Sentry.captureException(error);
}
